import { useEffect, useState } from "react";
import { RefreshCw, Link2, Unlink, Plus, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function PipelineSyncCard({ estimate, onRefresh }) {
  const id = estimate.id;
  const [state, setState] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => estApi.getPipelineState(id).then(setState);
  useEffect(() => { load().catch(() => {}); // eslint-disable-next-line
  }, [id]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res && res.state) setState(res);
      else await load();
      toast.success(msg);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const badge = {
    synced: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    unlinked: "bg-slate-100 text-slate-500",
  }[state.state];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="pipeline-sync-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Sales Pipeline</h3>
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${badge}`}
                data-testid="pipeline-sync-state">{state.state}</span>
          <label className="flex items-center gap-2 text-[11px] text-slate-500">
            Sync enabled
            <Switch checked={state.enabled} data-testid="pipeline-sync-toggle"
                    onCheckedChange={(v) => run(
                      () => estApi.savePipelineSettings({ enabled: v, mode: state.mode }),
                      v ? "Pipeline sync on" : "Pipeline sync paused")} />
          </label>
          <select value={state.mode} className="h-7 rounded border border-slate-200 px-1 text-[11px]"
                  onChange={(e) => run(
                    () => estApi.savePipelineSettings({ enabled: state.enabled, mode: e.target.value }),
                    `Mode: ${e.target.value}`)}
                  data-testid="pipeline-sync-mode">
            <option value="manual">manual</option>
            <option value="live">live</option>
          </select>
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Money flows out of the estimate (total sales, GM%, bid due, estimator, AM). Stage flows back
        in from the Pipeline. Nothing syncs while this is off.
      </p>

      {state.linked ? (
        <>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <Link2 className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-medium text-slate-700">{state.project?.name}</span>
            <span className="text-slate-400">
              {state.project?.status} · {money(state.project?.total_sales)}
            </span>
            <span className="ml-auto text-[11px] text-slate-400">
              {state.last_synced_at
                ? `last synced ${new Date(state.last_synced_at).toLocaleString()}`
                : "never synced"}
            </span>
          </div>
          {(state.diffs.length > 0 || state.stage_conflict) && (
            <div className="mt-2 space-y-0.5 rounded bg-amber-50 px-2 py-1.5"
                 data-testid="pipeline-diffs">
              {state.stage_conflict && (
                <div className="flex items-center gap-1.5 text-[11px] text-amber-800">
                  <AlertCircle className="h-3 w-3" /> stage differs — Pipeline says
                  {" "}{state.project?.status}, estimate says {estimate.status} (Pipeline wins)
                </div>
              )}
              {state.diffs.map((d) => (
                <div key={d.field} className="text-[11px] text-amber-800">
                  {d.field}: pipeline {String(d.pipeline ?? "—")} → estimate{" "}
                  {String(d.estimate ?? "—")}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <Button size="sm" className="text-xs" disabled={busy || !state.enabled}
                    onClick={() => run(() => estApi.syncPipeline(id), "Synced with the Pipeline")}
                    data-testid="pipeline-sync-btn">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Sync now
            </Button>
            <Button size="sm" variant="ghost" className="text-xs text-slate-500"
                    onClick={() => run(() => estApi.unlinkPipeline(id), "Unlinked")}
                    data-testid="pipeline-unlink-btn">
              <Unlink className="mr-1.5 h-3.5 w-3.5" /> Unlink
            </Button>
            {state.state === "synced" && (
              <span className="ml-auto flex items-center gap-1 text-[11px] text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> in sync
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[11px] text-slate-500">Existing Pipeline project</label>
            <select value={pick} onChange={(e) => setPick(e.target.value)}
                    className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs"
                    data-testid="pipeline-project-select">
              <option value="">Choose a project…</option>
              {state.candidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.customer_name} ({p.status})
                </option>
              ))}
            </select>
          </div>
          <Button size="sm" variant="outline" className="text-xs" disabled={!pick || busy}
                  onClick={() => run(() => estApi.linkPipeline(id, { project_id: pick }), "Linked")}
                  data-testid="pipeline-link-btn">
            <Link2 className="mr-1.5 h-3.5 w-3.5" /> Link
          </Button>
          <Button size="sm" className="text-xs" disabled={busy || !state.enabled}
                  onClick={() => run(() => estApi.linkPipeline(id, { create: true }),
                    "Pipeline project created")}
                  data-testid="pipeline-create-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Create new
          </Button>
        </div>
      )}
    </div>
  );
}
